import type React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { usePanelStore } from '../hooks/usePanelStore';
import type { PanelPlacement } from '../types/panelPlacement';
import { widgetId } from '../widgetId';

type PanelPortalProps = {
  messageId: string;
  componentName: string;
  params: unknown;
  placement: PanelPlacement;
};

/**
 * A portal that registers a widget in the panel store on mount and unregisters it on unmount.
 * The portal extracts the widget from the chat pipeline and places it in the dual panel layout.
 */
export const PanelPortal: React.FC<PanelPortalProps> = ({ messageId, componentName, params, placement }) => {
  const { registerWidget, unregisterWidget } = usePanelStore();
  const id = useMemo(() => widgetId({ messageId, componentName, params }), [messageId, componentName, params]);

  // Prevent rerenders from causing the widget to be unregistered and re-registered by storing the latest props in a ref.
  const latest = useRef({ messageId, componentName, params, placement });
  latest.current = { messageId, componentName, params, placement };

  useEffect(() => {
    const { messageId, componentName, params, placement } = latest.current;
    registerWidget({ id, messageId, componentName, params }, placement);
    return () => unregisterWidget(id);
  }, [id, registerWidget, unregisterWidget]);

  return null;
};
